import { type ComponentProps, type ReactNode, useCallback } from 'react';
import type {
    TOGGLE_GROUP_LAYOUT,
    TOGGLE_SIZE,
} from '@george-labs.com/design-system/types/components/Toggle';
import type { ICON } from '@george-labs.com/george-assets/icons';
import { FormField, Paragraph, Toggle, ToggleGroup } from '@george-labs.com/design-system';

import { useField } from '@luna/form2';
import _ from '@luna/lodash';

import type { FormFieldProps } from './types/FormFieldProps';
import { shouldShowError } from './FieldErrorVisibilityService';

export type RadioToggleItem = {
    name: string;
    value: string;
    label: string;
    description?: ReactNode;
    icon?: ICON;
    badgeSize?: TOGGLE_SIZE;
    toggleItemClassName?: string;
    disabled?: boolean;
    'data-cy'?: string;
};

interface Props<FormValues> extends FormFieldProps<FormValues> {
    items: RadioToggleItem[];
    layout?: TOGGLE_GROUP_LAYOUT;
    minColumns?: number;
    isErrorVisible?: boolean;
    inputProps?: Pick<ComponentProps<typeof ToggleGroup>, 'onToggle' | 'className'>;
    shouldClearOnUnmount?: boolean;
    error?: string;
}

const composeToggleButton = ({
    name,
    value,
    label,
    description,
    icon,
    toggleItemClassName,
    disabled,
    badgeSize,
    'data-cy': dataCy,
}: RadioToggleItem) => {
    const id = _.kebabCase(`${name}-${value}`);

    if (badgeSize) {
        return (
            <Toggle
                id={id}
                className={toggleItemClassName}
                key={value}
                size={badgeSize}
                name={name}
                value={value}
                label={label}
                description={description}
                disabled={disabled}
                data-cy={dataCy}
            />
        );
    }

    if (icon) {
        return (
            <Toggle
                id={id}
                className={toggleItemClassName}
                key={value}
                icon={icon}
                name={name}
                value={value}
                label={label}
                description={description}
                disabled={disabled}
                data-cy={dataCy}
            />
        );
    }

    return (
        <Toggle
            id={id}
            className={toggleItemClassName}
            key={value}
            name={name}
            value={value}
            label={label}
            description={description}
            disabled={disabled}
            data-cy={dataCy}
        />
    );
};

export const FormRadioToggleGroupField = <FormValues = void,>({
    alert,
    fieldName,
    items,
    label,
    labelPosition,
    required,
    description,
    layout,
    minColumns,
    'data-cy': dataCy,
    isErrorVisible = true,
    inputProps,
    warning,
    info,
    shouldClearOnUnmount,
    labelColumnWidth,
    variant,
    compact,
    error: errorOverride,
}: Props<FormValues>) => {
    const [{ value }, { error: validationError, touched }, { setValue, setTouched }] = useField(
        fieldName,
        {
            shouldClearOnUnmount,
        },
    );

    const isFieldErrorVisible = shouldShowError(isErrorVisible, touched, validationError);

    const toggledItems = value ? [value] : [];

    const onToggle = inputProps?.onToggle;

    const toggleRadioOption = useCallback(
        ([item]: string[]) => {
            setValue(item);
            setTouched();
            onToggle?.([item]);
        },
        [setValue, setTouched, onToggle],
    );

    return (
        <FormField
            alert={alert}
            data-cy={dataCy}
            label={label}
            labelPosition={labelPosition}
            required={required}
            description={description}
            labelColumnWidth={labelColumnWidth}
            input={ToggleGroup}
            variant={variant}
            compact={compact}
            inputProps={{
                type: ToggleGroup.TYPE.RADIO,
                onToggle: toggleRadioOption,
                toggledItems,
                layout,
                minColumns,
                className: inputProps?.className,
                children: items.map(composeToggleButton),
            }}
            error={
                (!!errorOverride || isFieldErrorVisible) && (
                    <Paragraph data-cy={`${dataCy}-error`}>
                        {errorOverride || validationError}
                    </Paragraph>
                )
            }
            warning={warning}
            info={info}
        />
    );
};

FormRadioToggleGroupField.LAYOUT = ToggleGroup.LAYOUT;
FormRadioToggleGroupField.LABEL_POSITION = FormField.LABEL_POSITION;
FormRadioToggleGroupField.VARIANT = FormField.VARIANT;
